import { CreatedProjects, ProjectAccessStepState, ProjectDetailsStepState, ProjectRow } from "../../../../../models";
import { bulkInviteOrganisationsToProjects } from "../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../service/Shared";
import { buildPlannedInvites, describeBulkInviteErrors, getProjectDeveloperUuid } from "./accessAssignments";
import { labelBulkErrorsByRow } from "./projectRowMappers";
import { callProjectDetails, hasCreatedAnything } from "./submitProjectDetails";

export type SaveNewProjectJourneyResult =
  // The create was refused, in whole or in part. The raw errors are kept so the caller can also
  // write them back against the details step's rows; `created` is what did get written - null when
  // nothing did - which the caller must keep so the retry sends only what is still missing.
  | { status: "createFailed"; errors: ServiceError[]; rawErrors: ServiceError[]; created: CreatedProjects | null }
  // The projects exist but the invitations were refused. `created` must be kept for the retry.
  | { status: "inviteFailed"; created: CreatedProjects; errors: ServiceError[] }
  | { status: "saved"; created: CreatedProjects; invitationCount: number };

interface SaveNewProjectJourneyInput {
  details: ProjectDetailsStepState;
  access: ProjectAccessStepState;
  /** What an earlier save already created, so a retry after a failed invite does not create again. */
  createdProjects: CreatedProjects | null;
  rows: ProjectRow[];
  projectNameByKey: Record<string, string>;
  creatingOrganisationUuid: string;
}

/*
  REQ-002 - create first with validateOnly false (the server re-validates, BR-02892), then invite. A
  retry after a failed invite, or after a create that only partly went through, reuses what was
  already created: posting it again would collide on the row uuids the api already accepted
  (BR-02888).

  Pure orchestration over the api: it reports what happened and leaves every state change, toast and
  navigation to the caller.
*/
export const saveNewProjectJourney = async ({
  details,
  access,
  createdProjects,
  rows,
  projectNameByKey,
  creatingOrganisationUuid,
}: SaveNewProjectJourneyInput): Promise<SaveNewProjectJourneyResult> => {
  // A project is created with a developer only where its access list names one other than the
  // creating organisation: that organisation is meant to hold the role from the start, so the
  // create gives it to them and buildPlannedInvites leaves them out of the invitations.
  //
  // Sent with what an earlier save already wrote: an upload of several landholdings is several
  // requests, and a retry after one of them was refused re-posts only that one.
  const result = await callProjectDetails(
    details,
    (projectKey) => getProjectDeveloperUuid(access.organisations, projectKey, creatingOrganisationUuid),
    false,
    createdProjects
  );

  if (result.errors.length > 0) {
    // Named by project: a create failure on the access step has no form to sit beside, so the
    // message has to say which of the session's projects it is about.
    return {
      status: "createFailed",
      errors: labelBulkErrorsByRow(result.errors, rows),
      rawErrors: result.errors,
      created: hasCreatedAnything(result.created) ? result.created : null,
    };
  }

  const { created } = result;

  const plan = buildPlannedInvites(
    access.organisations,
    access.responsibleOwners,
    created.projectUuidByKey,
    projectNameByKey,
    creatingOrganisationUuid
  );

  if (plan.length > 0) {
    const response = await bulkInviteOrganisationsToProjects({
      assignments: plan.map((planned) => planned.assignment),
      sourceOrganisationUuid: creatingOrganisationUuid,
    });

    if (response.status === Status.Error) {
      return { status: "inviteFailed", created, errors: describeBulkInviteErrors(response.errors, plan) };
    }
  }

  return { status: "saved", created, invitationCount: plan.length };
};
